import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/outline';
import { Collapse } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import PlayVideoCourseIcon from 'assets/Icons/PlayVideoCourseIcon';
import clsx from 'clsx';
import moment from 'moment';
import { useRouter } from 'next/router';
import { FC, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';

import CoursePreviewModal from '../CourseReviewSection/CoursePreviewModal';
import CourseHeading from '../../BookLandingPageComp/landingPage/heading/CourseHeading';
import { ShouldRender } from '../../shared/basic/ShouldRender';
import { user } from '../../../atoms/user';
import { User } from '../../../utils/interfaces/User';

const { Panel } = Collapse;

export type Section = {
  name: string;
  duration: string;
  videoLink?: string;
};

type Module = {
  name: string;
  lesson: number;
  duration: string;
  sections: Section[];
};

export interface CourseCurriculumSectionProps {
  data: Module[];
}

const customExpandIcon = ({ isActive }: { isActive?: boolean }) => (
  <span className='text-white'>
    {isActive ? (
      <ChevronUpIcon className='h-5 w-5 md:w-6 md:h-6 text-white translate-y-[80%]  md:translate-y-[70%]' />
    ) : (
      <ChevronDownIcon className='h-5 w-5 md:w-6 md:h-6 text-white translate-y-[80%]  md:translate-y-[70%]' />
    )}
  </span>
);

const formatDuration = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return hours > 0 ? `${hours} hr ${minutes} min` : `${minutes} min`;
};

const formatWithMoment = (totalSeconds: number): string => {
  const duration = moment.duration(totalSeconds, 'seconds');
  const hrs = duration.hours();
  const mins = duration.minutes();
  const secs = duration.seconds();

  const parts = [];
  if (hrs) parts.push(`${hrs} hr`);
  if (mins) parts.push(`${mins} min`);
  if (secs || parts.length === 0) parts.push(`${secs} sec`);

  return parts.join(' ');
};

const parseDuration = (duration: string): number => {
  if (!duration || typeof duration !== 'string') return 0;

  duration = duration.toLowerCase().replace(/,/g, '').trim();

  let totalSeconds = 0;

  const colonParts = duration.split(':').map((s) => s.trim());
  if (colonParts.length === 2 || colonParts.length === 3) {
    const [hh, mm, ss] = [0, 0, 0, ...colonParts].slice(-3).map(Number);
    return (hh || 0) * 3600 + (mm || 0) * 60 + (ss || 0);
  }

  const hrMatch = duration.match(/(\d+(?:\.\d+)?)\s*hr[s]?/);
  const minMatch = duration.match(/(\d+(?:\.\d+)?)\s*min(?:ute)?s?/);
  const secMatch = duration.match(/(\d+(?:\.\d+)?)\s*sec(?:ond)?s?/);

  if (hrMatch) totalSeconds += parseFloat(hrMatch[1] ?? '0') * 3600;
  if (minMatch) totalSeconds += parseFloat(minMatch[1] ?? '0') * 60;
  if (secMatch) totalSeconds += parseFloat(secMatch[1] ?? '0');

  if (totalSeconds === 0) {
    const fallback = parseFloat(duration);
    if (!isNaN(fallback)) totalSeconds = fallback;
  }

  return Math.round(totalSeconds);
};

const CourseCurriculumSection: FC<CourseCurriculumSectionProps> = ({
  data,
}) => {
  const [isModalCoursePreviewVisible, setIsModalCoursePreviewVisible] =
    useState<{ value: boolean; link: string | undefined }>({
      value: false,
      link: undefined,
    });
  const [activeKeys, setActiveKeys] = useState<string[]>([]);
  const userData: User | undefined = useRecoilValue(user);
  const screens = useBreakpoint();
  const router = useRouter();
  const { id } = router.query;

  // Open all modules by default
  useEffect(() => {
    setActiveKeys(data.map((module, index) => `${module?.name}-${index}`));
  }, [data]);

  const totalLessons = data.reduce((sum, module) => sum + module.lesson, 0);
  const totalDuration = data.reduce(
    (sum, module) =>
      sum +
      module.sections.reduce(
        (secSum, section) => secSum + parseDuration(section.duration),
        0,
      ),
    0,
  );

  const toggleExpandAll = () => {
    if (activeKeys.length === data.length) {
      setActiveKeys([]);
    } else {
      setActiveKeys(data.map((module, index) => `${module?.name}-${index}`));
    }
  };

  return (
    <div className='text-white'>
      <CourseHeading heading='Course Content' />
      <div className='flex flex-col md:flex-row justify-between'>
        <span className='block text-[#FFFFFF99] font-semibold text-xs md:text-sm pb-2 md:pb-3'>
          {data.length} Modules • {totalLessons} lectures •{' '}
          {formatDuration(totalDuration)} length
        </span>
        <div
          onClick={toggleExpandAll}
          className='text-xs md:text-sm font-semibold text-primary hover:underline pb-3'
        >
          {activeKeys.length === data.length ? 'Collapse All' : 'Expand All'}
        </div>
      </div>

      <div className='border-[1px] rounded-xl border-[#282829] overflow-hidden'>
        <Collapse
          prefixCls='course-landing-page'
          // accordion
          bordered={false}
          expandIconPosition='left'
          expandIcon={customExpandIcon}
          className=' customBorder border-[1px] rounded-xl border-[#282829] text-white'
          activeKey={activeKeys}
          onChange={(keys) => setActiveKeys(keys as string[])}
        >
          {data.map((module, index) => (
            <Panel
              key={`${module?.name}-${index}`}
              className='customCollapseCourse customBorder border-b-[#282829] '
              header={
                <div className='flex justify-between items-center pe-2 gap-x-3 md:gap-x-4 w-full'>
                  <div className='flex flex-col '>
                    <span className='font-medium text-grayZl-600 text-[10px]/[18px] uppercase tracking-[15%]'>
                      Module {index + 1}
                    </span>
                    <span className='font-semibold text-sm/[22px] md:text-base text-white'>
                      {module.name}
                    </span>
                  </div>
                  <div className='flex items-center gap-x-3 md:gap-x-4'>
                    <ShouldRender check={screens.md}>
                      <span className='text-[#9F9EA6] whitespace-nowrap font-medium text-sm'>
                        {module.sections.length > 0
                          ? `${module.sections.length} Lessons • ${formatWithMoment(parseDuration(module.duration))}`
                          : ''}
                      </span>
                    </ShouldRender>

                    <PlayVideoCourseIcon className='w-5 h-5 min-w-5 min-h-5 text-grayZl-500' />
                  </div>
                </div>
              }
            >
              {module.sections.length > 0 ? (
                <ul className=' flex flex-col sm:gap-y-2.5 gap-y-2 py-1.5 ps-4 border-t border-grayZl-900'>
                  {module.sections.map((lesson, index) => (
                    <li
                      key={`${lesson?.name}-${index}`}
                      className='flex justify-between items-center rounded-md'
                    >
                      <div className='flex items-center gap-x-5 w-full overflow-hidden'>
                        {/* <div
                          className={clsx(
                            lesson.videoLink ? 'bg-primary' : 'bg-[#3C3B3E]',
                            'h-6 w-2 rounded-t-xl rounded-b-xl -ml-1',
                          )}
                        /> */}
                        <div className='flex w-full justify-between gap-x-2 items-center'>
                          <div className='flex gap-x-4 group items-center'>
                            {/* <div
                              className={clsx(
                                lesson.videoLink &&
                                  !screens.md &&
                                  'group-hover:text-primary cursor-pointer',
                                'w-6 h-6 text-white',
                              )}
                            >
                              <PlayVideoCourseIcon />
                            </div> */}
                            <div className='w-1 min-w-1 h-1 min-h-1 rounded-full bg-white' />
                            <span
                              onClick={() => {
                                lesson.videoLink &&
                                  !screens.md &&
                                  setIsModalCoursePreviewVisible({
                                    value: true,
                                    link: lesson.videoLink,
                                  });
                              }}
                              className={clsx(
                                lesson.videoLink &&
                                  !screens.md &&
                                  'group-hover:text-primary cursor-pointer',
                                'text-white/60 text-sm/[22px] font-normal pr-4 md:pr-0',
                              )}
                            >
                              {lesson.name}
                            </span>
                          </div>{' '}
                          <ShouldRender check={screens.md}>
                            {lesson.videoLink && (
                              <span
                                onClick={() => {
                                  setIsModalCoursePreviewVisible({
                                    value: true,
                                    link: lesson.videoLink,
                                  });
                                }}
                                className='text-primary cursor-pointer text-sm underline font-medium whitespace-nowrap'
                              >
                                Preview Video
                              </span>
                            )}
                          </ShouldRender>
                        </div>
                        <ShouldRender check={screens.md}>
                          <span className='text-[#FFFFFF99] text-sm mr-6 whitespace-nowrap'>
                            {formatWithMoment(parseDuration(lesson.duration))}
                          </span>
                        </ShouldRender>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className='text-[#FFFFFF99] flex justify-center py-6 text-sm'>
                  No lessons available.
                </p>
              )}
            </Panel>
          ))}
        </Collapse>
      </div>

      <CoursePreviewModal
        open={isModalCoursePreviewVisible.value}
        courseId={id as string}
        userData={userData}
        onClose={() =>
          setIsModalCoursePreviewVisible({ value: false, link: undefined })
        }
        courseSampleLink={isModalCoursePreviewVisible?.link as string}
      />
    </div>
  );
};

export default CourseCurriculumSection;
